import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PlatformIcon } from "@/components/ui/platform-icon";
import { cn } from "@/lib/utils";
import { useLocale, formatNumber } from "@/i18n/format";
import type { AdBoost, AdsSummary } from "@/hooks/use-ads";

interface ActivateBoostDialogProps {
  boost: AdBoost | null;
  summary: AdsSummary | undefined;
  durationDays?: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  pending: boolean;
}

function usdFormatted(n: number, locale: string): string {
  return formatNumber(n, locale, { style: "currency", currency: "USD" });
}

/**
 * Real-money confirm. The one place window.confirm is not enough — the user
 * must see the budget impact against the monthly cap before money moves.
 */
export function ActivateBoostDialog({
  boost,
  summary,
  durationDays,
  open,
  onOpenChange,
  onConfirm,
  pending,
}: ActivateBoostDialogProps) {
  const { t } = useTranslation("ads");
  const locale = useLocale();

  if (!boost) return null;

  const rawBudget = boost.budgetUsd != null ? Number(boost.budgetUsd) : NaN;
  const budgetValid = Number.isFinite(rawBudget) && rawBudget > 0;
  const budget = budgetValid ? rawBudget : 0;
  const committed = Number(summary?.committedThisMonthUsd ?? 0);
  const cap = summary?.capUsd != null ? Number(summary.capUsd) : null;
  const after = committed + budget;
  // Red emphasis when activation lands within 10% of the cap (or over it).
  const nearCap = cap !== null && after >= cap * 0.9;

  const budgetStr = budgetValid ? usdFormatted(budget, locale) : "—";
  const committedStr = usdFormatted(committed, locale);
  const capStr = cap !== null ? usdFormatted(cap, locale) : t("activateDialog.noCap");
  const afterStr = usdFormatted(after, locale);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("activateDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("activateDialog.description")}
          </DialogDescription>
        </DialogHeader>

        {boost.piece && (
          <div className="flex items-center gap-3 rounded-md border p-3">
            {boost.piece.thumbnailUrl ? (
              <img
                src={boost.piece.thumbnailUrl}
                alt=""
                className="h-12 w-12 flex-shrink-0 rounded object-cover"
              />
            ) : (
              <div className="h-12 w-12 flex-shrink-0 rounded bg-muted" />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium">
                <PlatformIcon platform={boost.piece.platform} size={14} />
                {boost.piece.shortCode}
              </div>
              <p className="truncate text-xs text-muted-foreground">{boost.piece.caption}</p>
            </div>
          </div>
        )}

        <div className="space-y-1 text-sm">
          {!budgetValid && (
            <p className="rounded-md bg-red-500/10 p-2 text-xs text-red-400">
              {t("activateDialog.budgetInvalid")}
            </p>
          )}
          <p>
            {t("activateDialog.budgetLabel")}{" "}
            <strong>{budgetStr}</strong>
            {durationDays
              ? ` ${t("activateDialog.budgetLifetimeDays", { days: durationDays })}`
              : ` ${t("activateDialog.budgetLifetime")}`}
          </p>
          <p>
            {t("activateDialog.committedLabel")} <strong>{committedStr}</strong>{" "}
            {t("activateDialog.of")} <strong>{capStr}</strong>
          </p>
          <p className={cn(nearCap && "font-semibold text-red-400")}>
            {t("activateDialog.afterLabel")} <strong>{afterStr}</strong>{" "}
            {t("activateDialog.of")} <strong>{capStr}</strong>
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            {t("activateDialog.cancelButton")}
          </Button>
          <Button
            className="bg-red-600 text-white hover:bg-red-700"
            onClick={onConfirm}
            disabled={pending || !budgetValid}
          >
            {pending
              ? t("activateDialog.activatingButton")
              : t("activateDialog.activateButton", { amount: budgetStr })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
